import dayjs from 'dayjs';
import { deriveAuditedType } from '../../../modules/Dashboard/DashboardModules/AuditedFinancialsVisualisation/auditedTypeUtils';
import { buildOnePagerContext } from '../builders/context';
import { buildDealSummarySection } from '../builders/dealSummarySection';
import { buildManagementProfileSection } from '../builders/managementProfileSection';
import { buildFinancialSummarySection } from '../builders/financialSummarySection';
import { buildFinancialRatiosSection } from '../builders/financialRatiosSection';
import { buildDebtScheduleSection } from '../builders/debtScheduleSection';
import { buildCreditBureauSection } from '../builders/creditBureauSection';
import { buildBankingSection } from '../builders/bankingSection';

export const COMPANY_METRICS_DATA = 'companyMetricsData';

// ============================================================================
// One Pager Sections Configuration (per-PNO)
// ============================================================================
// Sections render in `order` ascending; entries with `visible: false` are skipped.
// `label` overrides the section heading. Unknown ids are ignored (defensive).
//
// `gstCustomersVendors` is a sub-section that appears as a row inside the
// Deal Summary table (when its parent section is visible AND the sub-section
// is visible). It is the only sub-section supported today.
//
// When the API returns no config for a PNO, callers should pass `null`/
// `undefined` for `sectionsConfig` and we fall back to `DEFAULT_ONE_PAGER_SECTIONS`.
export type OnePagerSectionId =
    | 'dealSummary'
    | 'managementProfile'
    | 'financialSummary'
    | 'financialRatios'
    | 'debtSchedule'
    | 'creditBureauSummary'
    | 'bankingSummary';

export type OnePagerSubSectionId =
    | 'legalEntityName'
    | 'dateOfIncorporation'
    | 'constitution'
    | 'sectorIndustry'
    | 'headquarters'
    | 'businessOverview'
    | 'previousInvestorsAndFundingStage'
    | 'gstCustomersVendors';

export interface OnePagerSubSectionConfig {
    id: OnePagerSubSectionId;
    visible: boolean;
    label: string;
    order?: number;
}

export interface OnePagerSectionConfig {
    id: OnePagerSectionId;
    visible: boolean;
    label: string;
    order?: number;
    subsections?: OnePagerSubSectionConfig[];
}

const KNOWN_SECTION_IDS: readonly OnePagerSectionId[] = [
    'dealSummary',
    'managementProfile',
    'financialSummary',
    'financialRatios',
    'debtSchedule',
    'creditBureauSummary',
    'bankingSummary',
];

const DEFAULT_DEAL_SUMMARY_SUBSECTIONS: OnePagerSubSectionConfig[] = [
    { id: 'legalEntityName', visible: true, label: 'Legal Entity Name', order: 1 },
    { id: 'dateOfIncorporation', visible: true, label: 'Date of incorporation', order: 2 },
    { id: 'constitution', visible: true, label: 'Constitution', order: 3 },
    { id: 'sectorIndustry', visible: true, label: 'Sector/Industry', order: 4 },
    { id: 'headquarters', visible: true, label: 'Headquarters', order: 5 },
    { id: 'businessOverview', visible: true, label: 'Business Overview', order: 6 },
    { id: 'previousInvestorsAndFundingStage', visible: true, label: 'Previous Investors and Funding Stage', order: 7 },
    { id: 'gstCustomersVendors', visible: true, label: 'GST Customers and Vendors', order: 8 },
];

export const DEFAULT_ONE_PAGER_SECTIONS: OnePagerSectionConfig[] = [
    {
        id: 'dealSummary',
        visible: true,
        label: 'Deal Summary',
        order: 1,
        subsections: DEFAULT_DEAL_SUMMARY_SUBSECTIONS,
    },
    { id: 'managementProfile', visible: true, label: 'Management Profile', order: 2 },
    { id: 'financialSummary', visible: true, label: 'Financial Summary', order: 3 },
    { id: 'financialRatios', visible: true, label: 'Financial Ratios', order: 4 },
    { id: 'debtSchedule', visible: true, label: 'Debt Schedule', order: 5 },
    { id: 'creditBureauSummary', visible: true, label: 'Credit Bureau Summary', order: 6 },
    { id: 'bankingSummary', visible: true, label: 'Banking Summary', order: 7 },
];

/**
 * Builds the one-pager editor value from API data. Resolves the per-PNO section
 * layout (falling back to DEFAULT_ONE_PAGER_SECTIONS), then dispatches each
 * visible section to its builder. Builders emit Plate-native nodes directly
 * (see OnePager/builders/plateNodes.ts), so the output loads without the
 * slateToPlate converter (isSlateShape === false).
 */
export function initialiseDealSummaryEditor(apiData: any, formData: null | Object = {}, sectionsConfig?: OnePagerSectionConfig[] | null) {
    const ctx = buildOnePagerContext(apiData, (formData ?? {}) as Record<string, any>);

    // Resolve the section list. Null/empty config → default layout (all sections visible).
    const resolvedSections = Array.isArray(sectionsConfig) && sectionsConfig.length ? sectionsConfig : DEFAULT_ONE_PAGER_SECTIONS;

    // Filter to visible & known ids, then sort by `order` (missing order → 0 → preserves
    // insertion order via stable sort).
    const visibleSections = resolvedSections
        .filter((s) => s?.visible && KNOWN_SECTION_IDS.includes(s.id))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // Derived suffixes — preserved from the legacy layout.
    const auditedSuffix = (() => {
        const derived = deriveAuditedType(
            ctx.audited_financial_balance_sheet?.latestConsolidatedFY ?? null,
            ctx.audited_financial_balance_sheet?.latestStandaloneFY ?? null,
        );
        return derived ? ` - ${derived}` : '';
    })();
    const debtScheduleSuffix = ctx.uw_debt_schedule_data?.asOfDate
        ? ` (as of ${dayjs(ctx.uw_debt_schedule_data.asOfDate).format("MMM'YY")})`
        : ctx.companyMetricsData?.lastUpdatedAt
        ? ` (as of ${dayjs(ctx.companyMetricsData.lastUpdatedAt).format("MMM'YY")})`
        : '';

    let updatedData: any[] = [];

    for (const section of visibleSections) {
        let blocks: any[] | null = null;
        switch (section.id) {
            case 'dealSummary':
                blocks = buildDealSummarySection(ctx, section);
                break;
            case 'managementProfile':
                blocks = buildManagementProfileSection(ctx, section);
                break;
            case 'financialSummary':
                blocks = buildFinancialSummarySection(ctx, section, auditedSuffix);
                break;
            case 'financialRatios':
                blocks = buildFinancialRatiosSection(ctx, section, auditedSuffix);
                break;
            case 'debtSchedule':
                blocks = buildDebtScheduleSection(ctx, section, debtScheduleSuffix);
                break;
            case 'creditBureauSummary':
                blocks = buildCreditBureauSection(ctx, section);
                break;
            case 'bankingSummary':
                blocks = buildBankingSection(ctx, section);
                break;
            default:
                // Unknown id — defensive, already filtered above. Skip.
                break;
        }
        if (blocks?.length) updatedData.push(...blocks);
    }

    // Trailing spacer. Plate-native `p` (not Slate `paragraph`) so the whole
    // generated value is uniformly Plate-shape and skips the slateToPlate
    // converter on load (isSlateShape === false).
    updatedData = [...updatedData, { type: 'p', children: [{ text: '' }] }];
    return updatedData;
}

export { processAuditedFinancialUnsecuredLoanData } from '../builders/helpers';
